import {
  IconButton,
  List,
  ListItem,
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Tooltip as MuiTooltip,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  colors,
  Link,
} from '@mui/material'
import { tableCellClasses } from '@mui/material/TableCell'
import React, { useState } from 'react'

import Actions from '../Actions/Actions'
import ContactForm from './ContactForm'
import { Add, Edit, Close, Star, ExpandMore } from '@mui/icons-material'
import TaskModal from '../../task/TaskModal'
import AccordionHeader from '../AccordionHeader'
import useAlert from '../../../hooks/useAlert'
import useContactTypes from '../../../hooks/api/useContactTypes'
import DeleteConfirmModal from '../DeleteConfirmModal'
import Tooltip from '../../../common/Tooltip'
import { formatToPhone } from '../../../utils/string'
import useIsMobile from '../../../hooks/useIsMobile'

const ContactList = ({
  commitContact,
  customer,
  contacts,
  removeContact,
  isNew,
  saveContacts,
  reset,
  handleCreateNewTicket,
  expandedAccordions,
  setExpandedAccordions,
  renderedSaveButton,
}) => {
  const [addingContact, setAddingContact] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [error, setError] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentContact, setCurrentContact] = useState(null)
  const [currentTaskType, setCurrentTaskType] = useState(null)
  const expanded = expandedAccordions.contacts
  const setExpanded = (val) =>
    setExpandedAccordions((prev) => ({ ...prev, contacts: val }))

  const [isEditing, setIsEditing] = useState(true)

  const [removingContact, setRemovingContact] = useState(null)

  const { createAlert, createGenericErrorAlert } = useAlert()

  const contactTypesQuery = useContactTypes()

  if (!contacts) return

  const previewContact = contacts.find((c) => c.isDefault) || contacts[0]

  const handleSaveContacts = () => {
    saveContacts()
      .then(() => {
        // setIsEditing(false)
        createAlert({ message: 'Contacts saved successfully' })
      })
      .catch(() => {
        createGenericErrorAlert()
      })
  }

  return (
    <>
      {!!removingContact && (
        <DeleteConfirmModal
          isOpen={!!removingContact}
          deleteString={`${removingContact?.firstName} ${removingContact?.lastName}`}
          onClose={() => setRemovingContact(null)}
          onSubmit={() => {
            removeContact(removingContact)
            setRemovingContact(null)
          }}
        ></DeleteConfirmModal>
      )}

      <Accordion
        id='contacts-header'
        elevation={8}
        sx={{ width: '100%', minWidth: '340px', maxWidth: '1250px' }}
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ mr: 4, display: 'flex', alignItems: 'center' }}>
              <b>Contacts</b>
              <Tooltip
                infoText={
                  'In this section, you can add, monitor, or remove contacts for this business profile. When you perform a "Task" for a contact, the system will automatically track and report those actions for you.'
                }
              />
            </Typography>

            {previewContact && !expanded && (
              <ContactTable
                contacts={[previewContact]}
                customer={customer}
                handleCreateNewTicket={handleCreateNewTicket}
                setRemovingContact={setRemovingContact}
                setEditingContact={setEditingContact}
                shouldStopProp
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionHeader
            additionalButtons={[
              {
                label: 'Add',
                endIcon: <Add />,
                onClick: () => {
                  // setIsEditing(true)
                  setAddingContact(true)
                },
                sx: { mt: -2 },
              },
            ]}
            isEditing={isEditing || isNew}
            setIsEditing={setIsEditing}
            onCancel={reset}
            onSave={handleSaveContacts}
          />

          {contacts.length === 0 && (
            <Box
              sx={{
                ml: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <i>No contacts</i>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <b>Record a Task</b>
                </Box>
                <Actions
                  customer={customer}
                  handleCreateNewTicket={handleCreateNewTicket}
                />
              </Box>
            </Box>
          )}
          {contacts.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mr: 1.5,
                mt: 2,
              }}
            >
              <ContactTable
                contacts={contacts}
                customer={customer}
                handleCreateNewTicket={handleCreateNewTicket}
                setRemovingContact={setRemovingContact}
                setEditingContact={setEditingContact}
              />
            </Box>
          )}

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mr: -2 }}
          >
            {renderedSaveButton && renderedSaveButton}
          </Box>
        </AccordionDetails>
      </Accordion>

      {(addingContact || editingContact) && (
        <ContactForm
          contactTypesQuery={contactTypesQuery}
          isOpen={addingContact || !!editingContact}
          onCommit={(contact) => {
            setAddingContact(false)
            setEditingContact(null)
            commitContact(contact)
              .then(() => {
                createAlert({ message: 'Contact saved successfully' })
              })
              .catch(createGenericErrorAlert)
          }}
          existingContact={editingContact}
          hasDefault={contacts.some((c) => c.isDefault)}
          handleClose={() => {
            setAddingContact(false)
            setEditingContact(null)
          }}
        />
      )}

      {isModalOpen && (
        <TaskModal
          customer={customer}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          contact={currentContact}
          taskType={currentTaskType}
        ></TaskModal>
      )}
    </>
  )
}

export default ContactList

const getLink = (socialMediaLink) => {
  return socialMediaLink.match(/^https?:/)
    ? socialMediaLink
    : '//' + socialMediaLink
}

const ContactTable = ({
  contacts,
  customer,
  handleCreateNewTicket,
  setRemovingContact,
  setEditingContact,
  shouldStopProp,
}) => {
  const isMobile = useIsMobile()

  const baseFontSize = isMobile ? 14 : 16

  return (
    <TableContainer
      sx={{ mt: -5 }}
      onClick={(e) => {
        if (shouldStopProp) e.stopPropagation()
      }}
    >
      <Table
        sx={{
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ pb: 0 }}></TableCell>
            <TableCell
              sx={{ textAlign: 'center', fontSize: `${baseFontSize}px`, pb: 0 }}
            >
              <b>Record a Task</b>
            </TableCell>
            <TableCell
              sx={{ textAlign: 'center', fontSize: `${baseFontSize}px`, pb: 0 }}
            >
              Edit
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contacts.map((c, i) => {
            return (
              <TableRow
                key={c.id}
                sx={{
                  backgroundColor: i % 2 === 0 ? 'white' : colors.grey[100],
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ ml: -2, mr: 1 }}>
                      {c.isDefault && <Star color='primary' />}
                    </Box>
                    <Box sx={{ fontSize: `${baseFontSize + 4}px` }}>
                      <div>
                        {c.firstName} {c.lastName}
                      </div>
                      <div>{c.title}</div>
                      <div>{c.emailAddress}</div>
                      <div>{formatToPhone(c.phoneNumber)}</div>
                      {c.socialMediaLink && (
                        <Link about='_blank' href={getLink(c.socialMediaLink)}>
                          {c.socialMediaLink}
                        </Link>
                      )}
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Actions
                        contact={c}
                        customer={customer}
                        handleCreateNewTicket={handleCreateNewTicket}
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    <MuiTooltip title='Delete'>
                      <IconButton onClick={() => setRemovingContact(c)}>
                        <Close />
                      </IconButton>
                    </MuiTooltip>
                    <MuiTooltip title='Edit'>
                      <IconButton onClick={() => setEditingContact(c)}>
                        <Edit />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
